import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { PlannedPayment, Frequency } from '../types';

interface PlannedPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  editingPayment?: PlannedPayment | null;
}

interface PaymentRow {
  id: string;
  date: string;
  description: string;
  amount: string;
}

type CommitmentType = 'custom' | 'income' | 'expense' | 'debt' | 'goal';

export default function PlannedPaymentModal({ isOpen, onClose, userId, householdId, editingPayment }: PlannedPaymentModalProps) {
  const [commitmentType, setCommitmentType] = useState<CommitmentType>('custom');
  
  // Custom
  const [commitmentName, setCommitmentName] = useState('');
  const [rows, setRows] = useState<PaymentRow[]>([]);
  
  // Recurring (Income / Expense)
  const [recurringName, setRecurringName] = useState('');
  const [recurringAmount, setRecurringAmount] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [date1, setDate1] = useState('1');
  const [date2, setDate2] = useState('15');
  const [nextDate, setNextDate] = useState('');
  
  // Debt
  const [debtType, setDebtType] = useState<'loan' | 'credit_card' | 'mortgage' | 'line_of_credit'>('credit_card');
  const [debtBalance, setDebtBalance] = useState('');
  const [debtMinimum, setDebtMinimum] = useState('');
  const [debtInterest, setDebtInterest] = useState('');
  
  // Goal
  const [goalTarget, setGoalTarget] = useState('');
  const [goalDeadline, setGoalDeadline] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (editingPayment) {
        setCommitmentType('custom');
        setCommitmentName(editingPayment.commitmentName);
        setRows([{
          id: '1',
          date: editingPayment.date.toISOString().split('T')[0],
          description: editingPayment.description,
          amount: editingPayment.amount.toString()
        }]);
      } else {
        setCommitmentType('custom');
        setCommitmentName('');
        setRows([{ id: Date.now().toString(), date: '', description: '', amount: '' }]);
        
        setRecurringName('');
        setRecurringAmount('');
        setFrequency('monthly');
        setDate1('1');
        setDate2('15');
        setNextDate('');
        
        setDebtType('credit_card');
        setDebtBalance('');
        setDebtMinimum('');
        setDebtInterest('');
        
        setGoalTarget('');
        setGoalDeadline('');
      }
      setError('');
    }
  }, [isOpen, editingPayment]);

  if (!isOpen) return null;

  const handleAddRow = () => {
    setRows([...rows, { id: Date.now().toString(), date: '', description: '', amount: '' }]);
  };

  const handleRemoveRow = (id: string) => {
    if (rows.length > 1) {
      setRows(rows.filter(r => r.id !== id));
    }
  };

  const handleRowChange = (id: string, field: keyof PaymentRow, value: string) => {
    setRows(rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (commitmentType === 'custom') {
        if (!commitmentName.trim()) { throw new Error('Commitment name is required.'); }
        const validRows = rows.filter(r => r.date && r.amount);
        if (validRows.length === 0) { throw new Error('At least one payment with a date and amount is required.'); }

        if (editingPayment) {
          const row = validRows[0];
          const dateObj = new Date(row.date + 'T12:00:00');
          await updateDoc(doc(db, 'planned_payments', editingPayment.id!), {
            commitmentName,
            description: row.description,
            date: dateObj,
            amount: parseFloat(row.amount),
          });
        } else {
          const batchPromises = validRows.map(row => {
            const dateObj = new Date(row.date + 'T12:00:00');
            return addDoc(collection(db, 'planned_payments'), {
              userId,
              householdId,
              commitmentName,
              description: row.description,
              date: dateObj,
              amount: parseFloat(row.amount),
              status: 'pending',
              createdAt: serverTimestamp()
            });
          });
          await Promise.all(batchPromises);
        }
      } else if (commitmentType === 'income') {
        if (!recurringName || !recurringAmount) throw new Error('Name and Amount are required.');
        // Was 'income_streams' -- a collection nothing in the app ever
        // reads. useBudgetData.ts/BudgetModal.tsx/Dashboard.tsx all read
        // and write 'incomes', so anything saved here was silently
        // invisible everywhere else in the app (no error, just orphaned).
        await addDoc(collection(db, 'incomes'), {
          userId,
          householdId,
          source: recurringName,
          amount: parseFloat(recurringAmount),
          schedule: {
            frequency,
            ...(frequency === 'monthly' || frequency === 'semi-monthly' ? { date1: parseInt(date1) } : {}),
            ...(frequency === 'semi-monthly' ? { date2: parseInt(date2) } : {}),
            ...(frequency === 'bi-weekly' || frequency === 'weekly' ? { nextDate: nextDate ? new Date(nextDate + 'T12:00:00') : null } : {})
          },
          createdAt: serverTimestamp()
        });
      } else if (commitmentType === 'expense') {
        if (!recurringName || !recurringAmount) throw new Error('Name and Amount are required.');
        await addDoc(collection(db, 'recurring_expenses'), {
          userId,
          householdId,
          category: recurringName,
          amount: parseFloat(recurringAmount),
          schedule: {
            frequency,
            ...(frequency === 'monthly' || frequency === 'semi-monthly' ? { date1: parseInt(date1) } : {}),
            ...(frequency === 'semi-monthly' ? { date2: parseInt(date2) } : {}),
            ...(frequency === 'bi-weekly' || frequency === 'weekly' ? { nextDate: nextDate ? new Date(nextDate + 'T12:00:00') : null } : {})
          },
          createdAt: serverTimestamp()
        });
      } else if (commitmentType === 'debt') {
        if (!recurringName || !debtBalance || !debtMinimum || !debtInterest) throw new Error('All fields are required.');
        await addDoc(collection(db, 'debts'), {
          userId,
          householdId,
          name: recurringName,
          type: debtType,
          totalAmount: parseFloat(debtBalance),
          currentAmount: parseFloat(debtBalance),
          monthlyMinimum: parseFloat(debtMinimum),
          interestRate: parseFloat(debtInterest),
          schedule: { frequency: 'monthly', date1: 1 }, // Default schedule for debts
          createdAt: serverTimestamp()
        });
      } else if (commitmentType === 'goal') {
        if (!recurringName || !goalTarget) throw new Error('Name and Target are required.');
        await addDoc(collection(db, 'goals'), {
          userId,
          householdId,
          name: recurringName,
          type: 'savings',
          targetAmount: parseFloat(goalTarget),
          currentAmount: 0,
          deadline: goalDeadline ? new Date(goalDeadline + 'T12:00:00') : null,
          createdAt: serverTimestamp()
        });
      }
      onClose();
    } catch (err: any) {
      console.error('Error saving:', err);
      setError(err.message || 'Failed to save commitment.');
    } finally {
      setLoading(false);
    }
  };

  const renderScheduleInputs = () => (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">Frequency</label>
        <select value={frequency} onChange={e => setFrequency(e.target.value as Frequency)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="monthly">Monthly</option>
          <option value="semi-monthly">Semi-Monthly</option>
          <option value="bi-weekly">Bi-Weekly</option>
          <option value="weekly">Weekly</option>
        </select>
      </div>
      {(frequency === 'monthly' || frequency === 'semi-monthly') && (
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Pay Date {frequency === 'semi-monthly' && '1'}</label>
          <input type="number" min="1" max="31" value={date1} onChange={e => setDate1(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
        </div>
      )}
      {frequency === 'semi-monthly' && (
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Pay Date 2</label>
          <input type="number" min="1" max="31" value={date2} onChange={e => setDate2(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
        </div>
      )}
      {(frequency === 'bi-weekly' || frequency === 'weekly') && (
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Next Occurrence</label>
          <input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-800">
            {editingPayment ? 'Edit Planned Payment' : 'Add Commitment Schedule'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
          {error && (
            <div className="p-3 bg-rose-50 text-rose-600 text-sm rounded-md border border-rose-100">
              {error}
            </div>
          )}

          {!editingPayment && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">What kind of commitment is this?</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                <button type="button" onClick={() => setCommitmentType('custom')} className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${commitmentType === 'custom' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Custom Schedule</button>
                <button type="button" onClick={() => setCommitmentType('expense')} className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${commitmentType === 'expense' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Fixed Expense</button>
                <button type="button" onClick={() => setCommitmentType('debt')} className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${commitmentType === 'debt' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Debt</button>
                <button type="button" onClick={() => setCommitmentType('goal')} className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${commitmentType === 'goal' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Savings Goal</button>
                <button type="button" onClick={() => setCommitmentType('income')} className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${commitmentType === 'income' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Expected Income</button>
              </div>
            </div>
          )}

          {commitmentType === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Commitment Name</label>
                <input type="text" value={commitmentName} onChange={e => setCommitmentName(e.target.value)} placeholder="e.g., Daughter's Dance Classes" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-slate-700">Payment Schedule</label>
                  {!editingPayment && (
                    <button type="button" onClick={handleAddRow} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Row</button>
                  )}
                </div>
                {rows.map(row => (
                  <div key={row.id} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <div className="w-full sm:w-1/3">
                      <label className="block text-xs font-medium text-slate-500 mb-1 sm:hidden">Date</label>
                      <input type="date" value={row.date} onChange={e => handleRowChange(row.id, 'date', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" required />
                    </div>
                    <div className="w-full sm:w-1/3">
                      <label className="block text-xs font-medium text-slate-500 mb-1 sm:hidden">Description</label>
                      <input type="text" value={row.description} onChange={e => handleRowChange(row.id, 'description', e.target.value)} placeholder="e.g., Registration Fee" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div className="w-full sm:w-1/3 flex items-center gap-2">
                      <div className="relative flex-1">
                        <label className="block text-xs font-medium text-slate-500 mb-1 sm:hidden">Amount</label>
                        <span className="absolute left-3 top-2 sm:top-2 text-slate-500">$</span>
                        <input type="number" step="0.01" min="0" value={row.amount} onChange={e => handleRowChange(row.id, 'amount', e.target.value)} placeholder="0.00" className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" required />
                      </div>
                      {!editingPayment && rows.length > 1 && (
                        <button type="button" onClick={() => handleRemoveRow(row.id)} className="p-2 text-slate-400 hover:text-rose-500 transition-colors mt-5 sm:mt-0" title="Remove row"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {(commitmentType === 'income' || commitmentType === 'expense') && (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">{commitmentType === 'income' ? 'Income Source' : 'Expense Category'}</label>
                <input type="text" value={recurringName} onChange={e => setRecurringName(e.target.value)} placeholder={commitmentType === 'income' ? 'e.g., Salary' : 'e.g., Netflix'} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-slate-500">$</span>
                  <input type="number" step="0.01" min="0" value={recurringAmount} onChange={e => setRecurringAmount(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
              </div>
              {renderScheduleInputs()}
            </>
          )}

          {commitmentType === 'debt' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Debt Name</label>
                  <input type="text" value={recurringName} onChange={e => setRecurringName(e.target.value)} placeholder="e.g., Chase Sapphire" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Debt Type</label>
                  <select value={debtType} onChange={e => setDebtType(e.target.value as any)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="credit_card">Credit Card</option>
                    <option value="loan">Loan</option>
                    <option value="mortgage">Mortgage</option>
                    <option value="line_of_credit">Line of Credit</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Total Balance</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-slate-500">$</span>
                    <input type="number" step="0.01" min="0" value={debtBalance} onChange={e => setDebtBalance(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Monthly Minimum</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-slate-500">$</span>
                    <input type="number" step="0.01" min="0" value={debtMinimum} onChange={e => setDebtMinimum(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Interest Rate</label>
                  <div className="relative">
                    <input type="number" step="0.01" min="0" value={debtInterest} onChange={e => setDebtInterest(e.target.value)} className="w-full pr-7 pl-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    <span className="absolute right-3 top-2 text-slate-500">%</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {commitmentType === 'goal' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Goal Name</label>
                <input type="text" value={recurringName} onChange={e => setRecurringName(e.target.value)} placeholder="e.g., Emergency Fund" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Target Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-slate-500">$</span>
                    <input type="number" step="0.01" min="0" value={goalTarget} onChange={e => setGoalTarget(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Target Date (Optional)</label>
                  <input type="date" value={goalDeadline} onChange={e => setGoalDeadline(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </>
          )}

          <div className="pt-4 mt-auto border-t border-slate-100 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md transition-colors" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50" disabled={loading}>
              {loading ? 'Saving...' : 'Save Commitment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
